<template>
  <div>
    <!--기본정보-->
    <field-select-module
      v-model="form.stdInfo"
      :board="board"
      :is-print="isPrint"/>

    <!-- 평가 정보 -->
    <div class="mt-28px">
      <h5 class="my-4"><i class="dot"></i> 평가 정보</h5>
      <div class="writeGridContainer">
        <div>
          <div class="form-title">평가자</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.평가자 }}</p>
          <ml-input v-else :readonly="board.readonly" v-model="form.평가자" :max="20"/>
        </div>
        <div>
          <div class="form-title">평가일</div>
          <p v-if="isPrint" class="print-input">{{ form.평가일 }}</p>
          <date-selector v-else :readonly="board.readonly" v-model="form.평가일" />
        </div>
      </div>
    </div>

    <!-- 피평가자 -->
    <div class="mt-28px">
      <h5 class="my-4"><i class="dot"></i> 피평가자</h5>
      <div class="writeGridContainer">
        <div>
          <div class="form-title">업체명</div>
          <p v-if="isPrint" class=" print-input">{{ form.companyName }}</p>
          <company-selector
            v-else
            :readonly="board.readonly"
            v-model="form.업체명"
            :company-name="form.companyName"
            class="w-full"
            :field-id="form.stdInfo.field_id"
            @select="targetCompany"
          />
        </div>
        <div>
          <div class="form-title">수급업무</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.수급업무 }}</p>
          <ml-input v-else :readonly="board.readonly" v-model="form.수급업무" />
        </div>
        <div>
          <div class="form-title">인원수</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.인원수 }}</p>
          <number-input v-else :readonly="board.readonly" v-model="form.인원수" />
        </div>
      </div>
    </div>

    <!-- 사내 수급업체 수행평가표 -->
    <div class="mt-28px flex justify-between">
      <h5 class="my-4"><i class="dot"></i> 사내 수급업체 수행평가표</h5>
      <span style="font-weight: 400; font-size: 14px; align-content: center;">※ 안전보건 수준 평가 Max 점수(가점 포함) ≤ 100점</span>
    </div>
    <div>
      <table>
        <colgroup>
          <col />
          <col width="120px" />
          <col width="120px" />
        </colgroup>
        <thead>
          <tr>
            <th>평가항목</th>
            <th>배점</th>
            <th>평가점수</th>
          </tr>
        </thead>

        <tbody>
        <!-- 안전예방지수 -->
        <tr>
          <th @click="addProtection">
            <div style="display: flex; align-items: center;">
              <vs-icon icon-pack="feather" icon="icon-plus" size="small" style="color: #4839EB; margin-right: 5px;"></vs-icon>
              <span>안전예방지수</span>
            </div>
          </th>
          <th>{{form.수행평가표.안전예방지수.score}}</th>
          <th>{{form.수행평가표.안전예방지수.point}}</th>
        </tr>
        <tr>
          <td colspan="3">
            <span v-html="form.수행평가표.안전예방지수.title" />
          </td>
        </tr>
        <tr v-for="(item, itemIndex) in form.수행평가표.안전예방지수.items" :key="`p_item_${itemIndex}`">
          <td>
            <div style="display: flex; align-items: center;">
              <vs-icon icon-pack="feather" icon="icon-minus" size="small" style="color: #4839EB; margin-right: 5px;" @click="removeProtection(item.id)"></vs-icon>
              <ml-input class="w-full" v-model="item.title" :max="300" :visible-counter="true"/>
            </div>
          </td>
          <td class="p-2">
            <number-input v-model="item.score" :min="0" :max="form.수행평가표.안전예방지수.score"/>
          </td>
          <td class="p-2">
            <number-input v-model="item.point" :min="0" :max="item.score"/>
          </td>
        </tr>

        <!-- 재해지수 -->
        <tr>
          <th>
            <div style="display: flex; align-items: center;">
              <span>재해지수</span>
            </div>
          </th>
          <th>{{form.수행평가표.재해지수.score}}</th>
          <th>{{form.수행평가표.재해지수.point}}</th>
        </tr>
        <tr>
          <td>
            <div>
              <p class="flex" style="align-items: center;">
                · 환산 재해율 = 환산재해 건 수/ 인원<vx-tooltip title="가능성이란?" :html="'자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요'" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px; color: #4839EB; margin-top: 2px;" @click="onClickToolTip1()" />
              </vx-tooltip>
              </p>
              <p>· 재해 A/B/C 등급별 구분, <span style="color:#EA5455;">사망사고 : 0점</span></p>
              <p class="mx-5">- 건수 :  A등급(1.5건), B등급(1건), C등급(0.5건)</p>
              <p class="mx-5">※ DKS 사규 재해 등급 기준 : 최초 요양 일수 기준 (의사 소견서 의무 제출)</p>
            </div>
          </td>
          <td class="p-2 text-center">
            <span>{{ form.수행평가표.재해지수.score }}</span>
          </td>
          <td class="p-2">
            <number-input v-model="form.수행평가표.재해지수.point" :min="0" :max="form.수행평가표.재해지수.score"/>
          </td>
        </tr>

        <!-- 안전경영지수 -->
        <tr>
          <th>
            <div style="display: flex; align-items: center;">
              <span>안전경영지수</span>
            </div>
          </th>
          <th>{{form.수행평가표.안전경영지수.score}}</th>
          <th>{{form.수행평가표.안전경영지수.point}}</th>
        </tr>
        <tr>
          <td>
            <div>
              <p>· KOSHA-MS (10점), 안전관리대행 (5점), 보건관리대행 (5점), 안전보건관리계획·실적(10점)</p>
              <p class="mx-5">- 미이행시 : 해당 배점(0점) ※ KOSHA-MS 인증 준비 : 중간점수 (5점) 반영</p>
              <p class="mx-5" style="color: #4839EB;">※ 식당/경비/통근버스 업체 : 안전보건관리계획 ·실적서 서류평가(절대평가:15~30점)로 대체</p>
            </div>
          </td>
          <td class="p-2 text-center">
            <span>{{ form.수행평가표.안전경영지수.score }}</span>
          </td>
          <td class="p-2">
            <number-input v-model="form.수행평가표.안전경영지수.point" :min="0" :max="form.수행평가표.안전경영지수.score"/>
          </td>
        </tr>

        <!-- 가감점 -->
        <tr>
          <th colspan="3" @click="addIncreaseAndDecrease">
            <div style="display: flex; align-items: center;">
              <vs-icon icon-pack="feather" icon="icon-plus" size="small" style="color: #4839EB; margin-right: 5px;"></vs-icon>
              <span>가감점</span>
            </div>
          </th>
        </tr>
        <tr>
          <td colspan="3">
            <span v-html="form.수행평가표.가감점.title" />
          </td>
        </tr>
        <tr v-for="(item, itemIndex) in form.수행평가표.가감점.items" :key="`iad_item_${itemIndex}`">
          <td>
            <div style="display: flex; align-items: center;">
              <vs-icon icon-pack="feather" icon="icon-minus" size="small" style="color: #4839EB; margin-right: 5px;" @click="removeIncreaseAndDecrease(item.id)"></vs-icon>
              <ml-input class="w-full" v-model="item.title" :max="300" :visible-counter="true"/>
            </div>
          </td>
          <td></td>
          <td class="p-2">
            <number-input v-model="item.point" :min="-7" :max="3"/>
          </td>
        </tr>

        </tbody>
        <tfoot>
          <tr>
            <td>총계</td>
            <td class="text-center">{{ form.총배점 }}</td>
            <td class="text-center">{{ form.총평가점수 }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div class="mt-4">
      <table>
        <thead>
        <tr>
          <th width="140">
            Incentive
            & Penalty
          </th>
          <td>
            <p><b>· Incentive</b>: <span style="color: #4839EB">사내 수급업체 안전보건 평가 결과에 따른 포상 추천</span>(사업장별), 전사 최상위업체 선정(안전환경기획팀)</p>
            <p class="ml-5">→ 경영책임자표창 및 포상금 지급 (200~100만원, 사업장 규모 차등 지급 - 근로자수 50인 이상 : 200만원, 50인 미만 : 100만원)</p>
            <p><b>· Penalty</b>: <span style="color: #EA5455">평가 결과 70점 미만시</span> 안전환경위원회 심의 (<span style="color: #EA5455">계약 연장 여부</span>)</p>
          </td>
        </tr>
        </thead>
      </table>
    </div>

    <!-- 파일첨부 -->
    <file-upload-module :readonly="board.readonly" v-model="form.fileList" :is-print="isPrint" class="mt-8" />

    <popup-convert-disaster v-model="showInfoPopup1" />
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import CompanySelector from "@/components/selector/CompanySelector.vue";
import NumberInput from "@/components/NumberInput.vue";
import PopupConvertDisaster from "@/views/project/popup/PopupConvertDisaster.vue";
import {required} from "vuelidate/lib/validators";

export default {
  components: {
    PopupConvertDisaster,
    NumberInput,
    CompanySelector,
    DateSelector,
    FileUploadModule,
    FieldSelectModule,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  validations: {
    form: {
      수행평가표: {
        안전예방지수: {
          items: {
            required,
            $each: {
              score: {
                required,
                if(value, vm) {
                  // 수행평가표 안전예방지수 내의 총배점이 각배점합산값과 같지 않은 경우
                  let totalScore = 0
                  for(const score of this.form.수행평가표.안전예방지수.items) {
                    totalScore += score.score
                  }

                  if (this.form.수행평가표.안전예방지수.score != totalScore) {
                    this.warningSafetyPrevention = true
                    return false;
                  } else {
                    this.warningSafetyPrevention = false
                  }
                  return true;

                },
              },
            },
          },
        },

      },
    },
  },
  data() {
    return {
      showInfoPopup1: false,

      warningSafetyPrevention: false,

      form: {
        stdInfo: {},
        fileList: [],

        평가자: "",
        평가일: "",
        업체명: "",
        companyName: "",
        수급업무: "",
        인원수: "",

        수행평가표: {
          안전예방지수: {
            title: '<p>[상대평가] 안전예방활동 평가(사업장 안전환경팀) → 취합/검증 (안전환경기획팀)</p>' +
              '<p>· 평가 항목 : 자율 지정   (ex. 안전경고장/계도장, 불안전 행동, 시설물 개선 등) →  업체별 등급배분표 참조(첨부 2)</p>',
            score: 30,
            point: 0,
            items: [],
          },
          재해지수: {
            score: 40,
            point: 0,
          },
          안전경영지수: {
            score: 30,
            point: 0,
          },
          가감점: {
            title: '<p>· 가점 : 1. 안전보건 우수활동 수상(3점: 장관상이상, 2점 : 안전보건공단 이사장상)</p>' +
              '<p style="padding: 0 48px;">2. 건강증진 우수사업장(1점, 인증/갱신 연도적용), 위험성평가 인증(1점), 전년도 대중소 상생협력사업 우수사업장(1점)</p>' +
              '<p class="mt-2">· 감점 : <span style="color: #EA5455;">산재은폐 (-7점/건), 산재미보고(-5점/건), 산재지연보고 (-3점/건, A등급 24시간, B,C등급 3일)</span></p>' +
              '<p style="padding: 0 48px; color: #4839EB;">※ 산재 은폐/미보고 : 노동부 행정처분 관점, 산재지연보고 : 사내 속보 보고 기준</p>',
            items: [
              { id: 1, title: '가점 : 안전보건 우수활동 수상 +3', point: 3 },
              { id: 2, title: '감점 : 산재미보고 -5', point: -5 },
            ],
          },
        },
        총안전예방지수: 0,
        총재해지수: 0,
        총안전경영지수: 0,
        총가감점: 0,
        총배점: 100,
        총평가점수: 0,
      },
    };
  },
  computed: {
  },
  watch: {
    "value": {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    "form": {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
    'form.수행평가표.안전예방지수': {
      immediate: true,
      deep: true,
      handler(value) {
        let totalPoint = 0;
        for (const item of value.items) {
          totalPoint += item.point;
        }
        this.form.총안전예방지수 = totalPoint;
        this.form.총평가점수 = this.form.총안전예방지수 + this.form.총재해지수 + this.form.총안전경영지수 + this.form.총가감점;
      },
    },
    'form.수행평가표.재해지수': {
      immediate: true,
      deep: true,
      handler(value) {
        this.form.총재해지수 = this.form.수행평가표.재해지수.point;
        this.form.총평가점수 = this.form.총안전예방지수 + this.form.총재해지수 + this.form.총안전경영지수 + this.form.총가감점;
      },
    },
    'form.수행평가표.안전경영지수': {
      immediate: true,
      deep: true,
      handler(value) {
        this.form.총안전경영지수 = this.form.수행평가표.안전경영지수.point;;
        this.form.총평가점수 = this.form.총안전예방지수 + this.form.총재해지수 + this.form.총안전경영지수 + this.form.총가감점;
      },
    },
    'form.수행평가표.가감점': {
      immediate: true,
      deep: true,
      handler(value) {
        let totalPoint = 0;
        for (const item of value.items) {
          totalPoint += item.point;
        }
        this.form.총가감점 = totalPoint;
        this.form.총평가점수 = this.form.총안전예방지수 + this.form.총재해지수 + this.form.총안전경영지수 + this.form.총가감점;
      },
    },
  },
  created() {
    const keys = Object.keys(this.form);
    for (const key of keys) {
      if (this.value[key]) {
        this.form[key] = this.value[key];
      }
    }
  },
  methods: {
    targetCompany (value) {
      this.form.companyName = value.com_name
    },

    addProtection () {
      this.form.수행평가표.안전예방지수.items.push({ id: Date.now(), title: '', score: 0, point: 0 });
    },
    removeProtection(id) {
      const index = this.form.수행평가표.안전예방지수.items.findIndex(item => item.id === id);
      if (index !== -1) {
        this.form.수행평가표.안전예방지수.items.splice(index, 1);
      }
    },

    addIncreaseAndDecrease () {
      this.form.수행평가표.가감점.items.push({ id: Date.now(), title: '', point: 0 });
    },
    removeIncreaseAndDecrease(id) {
      const index = this.form.수행평가표.가감점.items.findIndex(item => item.id === id);
      if (index !== -1) {
        this.form.수행평가표.가감점.items.splice(index, 1);
      }
    },

    onClickToolTip1 () {
      this.showInfoPopup1 = true
    },

    validate() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        if (this.form.수행평가표.안전예방지수.items.length === 0) {
          return "안전예방지수 항목을 추가해주세요"
        }
        if (this.warningSafetyPrevention) {
          return "안전예방지수 배점을 확인해주세요"
        }
      }
    },
  }
};
</script>
<style lang="scss" scoped>

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #cdcdcd;
  }

  thead, tbody {
    th {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      text-align: center;
      color: #3c3c3c;
      background: #f9f9f9;
      padding: 10px;
    }

    td {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #3c3c3c;
      padding: 10px;
    }
  }

  tfoot {
    td {
      font-weight: 500;
      font-size: 14px;
      color: #3c3c3c;
      background: #f9f9f9;
      padding: 10px
    }
  }

}

</style>
